import { PostBlockController, usePostBlockController, usePostSearchController } from '@nucleus/post-theme-elements';
// NOTE: This component uses @nucleus/sermon-theme-elements as placeholders until @nucleus/post-theme-elements is created
import { MediaGrid, MediaList, MediaListToolbar, ViewToggle } from '@nucleus/sermon-theme-elements';
import { PostBlockWeb, SectionTypeEnum } from '@nucleus/types/web';
import { PostSearchResultsBlockWeb } from '@nucleus/types/web/sections/post-search-results';
import { Text, nucleusClass } from '@nucleus/web-theme-elements';
import classNames from 'classnames';
import { merge as _merge, omit as _omit } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { SectionButton } from '../components/Button';
import { LoadingSpinnerBlock } from '../components/LoadingSpinner';
import { PostSearchUrlController } from '../controllers/PostSearchUrlController';
import { BlockProps } from '../types/component';
import { BlockInfo } from './BlockInfo';
import { BlockPost, GridViewItem, ListViewItem, PostBlockShareMenu } from './BlockPost';
import { PostBlockSkeleton } from './PostBlockSkeleton';

export type BlockPostSearchResultsProps = Extract<
  BlockProps,
  { block?: PostSearchResultsBlockWeb; sectionType?: 'postSearchResults' }
>;
type BlockPostProps = Extract<BlockProps, { block?: PostBlockWeb; sectionType?: 'post' }>;

export const BlockPostSearchResults = (props: BlockPostSearchResultsProps): JSX.Element => {
  return (
    <React.Suspense
      fallback={
        <div className={classNames([props.className, nucleusClass('block-posts-search-results')])}>
          <ColumnDiv>
            <PostBlockSkeleton layout="grid" />
          </ColumnDiv>
        </div>
      }
    >
      <PostSearchUrlController engineId={props.block?.engineId}>
        <PostBlockController {...props.block}>
          <PostsBlock {...props} />
        </PostBlockController>
        <FallbackNoResults {...props} />
      </PostSearchUrlController>
    </React.Suspense>
  );
};

const PostsBlock = (props: BlockPostSearchResultsProps) => {
  const { searchQuery, status } = usePostSearchController();

  if (searchQuery === '') {
    return null;
  }

  const renderLoading = status === 'loading';
  const renderResults = status === 'success';

  return (
    <div className={classNames([props.className, nucleusClass('block-posts-search-results')])}>
      <ColumnDiv>
        <ViewWrapper {...props}>
          {renderLoading && <LoadingSpinnerBlock />}
          {renderResults && <SwitchPostListLayout />}
          <NoResults {...props} />
        </ViewWrapper>
      </ColumnDiv>
    </div>
  );
};

const NoResults = (props: BlockPostSearchResultsProps): JSX.Element | null => {
  const { total, status } = usePostSearchController();

  if (status === 'loading') {
    return null;
  }

  if (total > 0) {
    return null;
  }

  return <BlockInfo {...props} blockHeadlineMaxWidth="small" />;
};

const FallbackNoResults = (props: BlockPostSearchResultsProps): JSX.Element | null => {
  const { total } = usePostSearchController();

  const blockProps = useRecentPostsProps(props);

  if (total > 0) {
    return null;
  }

  return <BlockPost {...blockProps} />;
};

const SwitchPostListLayout = () => {
  const { currentLayout } = usePostBlockController();

  switch (currentLayout) {
    case 'list':
      return <ListView />;
    case 'grid':
    default:
      return <GridView />;
  }
};

const isListOrGrid = (value: string): value is 'list' | 'grid' => ['list', 'grid'].includes(value);

interface ViewWrapperProps extends BlockPostSearchResultsProps {
  children: React.ReactNode;
}
const ViewWrapper = (props: ViewWrapperProps) => {
  const { currentLayout } = usePostBlockController();
  const { hasNextPage, fetchNextPage, isFetchingNextPage } = usePostSearchController();

  const layout = isListOrGrid(currentLayout) ? currentLayout : 'grid';

  return (
    <>
      <PostListViewToolbar currentLayout={layout} />
      {props.children}
      {hasNextPage && (
        <SectionButton
          style={{ width: '100%' }}
          onClick={fetchNextPage}
          loading={isFetchingNextPage}
          disabled={isFetchingNextPage}
        >
          Load More
        </SectionButton>
      )}
    </>
  );
};

const ListView = () => {
  const { posts, getPostPath } = usePostSearchController();

  return (
    <MediaList className={nucleusClass('media-list')}>
      {posts.map((post) => (
        <ListViewItem key={post.id} post={post as any} getPostPath={getPostPath} />
      ))}
    </MediaList>
  );
};

const GridView = () => {
  const { posts, getPostPath } = usePostSearchController();

  return (
    <MediaGrid style={{ marginBottom: '36px' }}>
      {posts.map((post) => (
        <GridViewItem key={post.id} post={post as any} getPostPath={getPostPath} />
      ))}
    </MediaGrid>
  );
};

type ToolbarProps = Parameters<typeof MediaListToolbar>[0];

const PostListViewToolbar = styled((props: ToolbarProps & { currentLayout: 'grid' | 'list' }) => {
  const { posts } = usePostSearchController();
  const { onLayoutChange } = usePostBlockController();

  const renderShareMenu = posts.length > 0;

  return (
    <MediaListToolbar
      {...props}
      left={
        <>
          <ViewToggle value={props.currentLayout} onChange={onLayoutChange} />
          <ResultsOutput />
        </>
      }
      right={<>{renderShareMenu && <PostBlockShareMenu />}</>}
    />
  );
})``;

const ResultsOutput = (): JSX.Element => {
  const { posts } = usePostSearchController();
  const total = posts?.length ?? 0;
  const text = total === 0 ? 'None' : total;

  return (
    <Text.L3 style={{ margin: 0 }}>
      <strong>Results:</strong> {text}
    </Text.L3>
  );
};

const ColumnDiv = styled.div`
  max-width: 1600px;
  margin-left: auto;
  margin-right: auto;
`;

const useRecentPostsProps = (props: BlockPostSearchResultsProps): BlockPostProps => {
  const { limit } = usePostSearchController();
  const base = _omit(props, 'block', 'sectionType');

  const block: PostBlockWeb = {
    isPostListNavVisible: false,
    engineId: props.block?.engineId,
    dataSource: {
      isLoadMoreEnabled: false,
      itemType: 'post',
      limit: limit,
      order: 'desc',
      orderBy: 'date',
      type: 'dynamic',
    },
    title: 'Recent Posts',
  };

  return _merge({ sectionType: SectionTypeEnum.Post }, base, {
    block: block,
  }) as BlockPostProps;
};
